// client/src/hooks/usePacotesCliente.js
import React, { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api';
import useAlert from './useAlert';

const PACOTES_CLIENTE_QUERY_KEY = 'pacotesCliente';

/**
 * Hook para buscar um único pacote de cliente por ID
 */
export const usePacoteCliente = (pacoteId) => {
    return useQuery({
        queryKey: [PACOTES_CLIENTE_QUERY_KEY, pacoteId],
        queryFn: async () => {
            if (!pacoteId) return null;
            const { data } = await apiClient.get(`/pacotes/${pacoteId}`);
            return data.data;
        },
        enabled: !!pacoteId,
    });
};

/**
 * Hook base para buscar pacotes de clientes
 */
export const usePacotesCliente = (params = {}) => {
    return useQuery({
        queryKey: [PACOTES_CLIENTE_QUERY_KEY, params],
        queryFn: async () => {
            const { data } = await apiClient.get('/pacotes', { params });
            return data;
        },
        enabled: params?.cliente ? true : (params.enabled !== undefined ? params.enabled : true),
    });
};

/**
 * Hook com filtros avançados para pacotes de clientes
 */
export const usePacotesClienteFiltrados = (filtros = {}) => {
    const params = useMemo(() => {
        const baseParams = {};
        
        // Aplicar filtros
        if (filtros.cliente) baseParams.cliente = filtros.cliente;
        if (filtros.status) baseParams.status = filtros.status;
        
        // ✅ AJUSTE: O filtro agora é por 'oferta', em vez de 'servico'.
        if (filtros.oferta) baseParams.oferta = filtros.oferta;

        if (filtros.dataInicio) baseParams.dataInicio = filtros.dataInicio;
        if (filtros.dataFim) baseParams.dataFim = filtros.dataFim;
        if (filtros.limit) baseParams.limit = filtros.limit;
        if (filtros.page) baseParams.page = filtros.page;
        if (filtros.sort) baseParams.sort = filtros.sort;
        if (filtros.enabled !== undefined) baseParams.enabled = filtros.enabled;
        
        return baseParams;
    }, [filtros]);

    return usePacotesCliente(params);
};

/**
 * Hook para pacotes ativos de um cliente específico
 */
export const usePacotesAtivosCliente = (clienteId, filtrosAdicionais = {}) => {
    return usePacotesClienteFiltrados({
        cliente: clienteId,
        status: 'Ativo',
        ...filtrosAdicionais
    });
};

/**
 * Hook para pacotes concluídos de um cliente
 */
export const usePacotesConcluidosCliente = (clienteId, filtrosAdicionais = {}) => {
    return usePacotesClienteFiltrados({
        cliente: clienteId,
        status: 'Concluído',
        ...filtrosAdicionais
    });
};

/**
 * Hook para pacotes cancelados de um cliente
 */
export const usePacotesCanceladosCliente = (clienteId, filtrosAdicionais = {}) => {
    return usePacotesClienteFiltrados({
        cliente: clienteId,
        status: 'Cancelado',
        ...filtrosAdicionais
    });
};

/**
 * ✅ AJUSTE: Hook renomeado e atualizado para filtrar por OFERTA.
 * Hook para pacotes por oferta específica
 */
export const usePacotesPorOferta = (ofertaId, clienteId = null, filtrosAdicionais = {}) => {
    const filtros = { oferta: ofertaId, ...filtrosAdicionais };
    if (clienteId) filtros.cliente = clienteId;
    
    return usePacotesClienteFiltrados(filtros);
};

/**
 * Hook para pacotes com status específico
 */
export const usePacotesPorStatus = (status, clienteId = null, filtrosAdicionais = {}) => {
    const filtros = { status, ...filtrosAdicionais };
    if (clienteId) filtros.cliente = clienteId;
    
    return usePacotesClienteFiltrados(filtros);
};

/**
 * Hook para histórico de pacotes por período
 */
export const usePacotesPorPeriodo = (dataInicio, dataFim, clienteId = null, filtrosAdicionais = {}) => {
    const filtros = { dataInicio, dataFim, ...filtrosAdicionais };
    if (clienteId) filtros.cliente = clienteId;
    
    return usePacotesClienteFiltrados(filtros);
};

/**
 * Hook para todos os pacotes de um cliente (independente do status)
 */
export const useTodosPacotesCliente = (clienteId, filtrosAdicionais = {}) => {
    return usePacotesClienteFiltrados({
        cliente: clienteId,
        ...filtrosAdicionais
    });
};

/**
 * Hook que retorna as funções de mutação para os pacotes dos clientes
 */
export const useMutatePacotesCliente = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    const invalidateCache = () => {
        queryClient.invalidateQueries({ queryKey: [PACOTES_CLIENTE_QUERY_KEY] });
    };

    const addPacote = useMutation({
        // A função espera um payload como: { clienteId, ofertaId, pacoteDePrecoId }
        mutationFn: (pacoteData) => apiClient.post('/pacotes', pacoteData),
        onSuccess: () => {
            invalidateCache();
            setAlert('Pacote vendido com sucesso!', 'success');
        },
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao vender pacote', 'danger'),
    });

    const updatePacote = useMutation({
        mutationFn: ({ id, pacoteData }) => apiClient.put(`/pacotes/${id}`, pacoteData),
        onSuccess: () => {
            invalidateCache();
            setAlert('Pacote atualizado com sucesso!', 'success');
        },
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao atualizar pacote', 'danger'),
    });

    const deletePacote = useMutation({
        mutationFn: (id) => apiClient.delete(`/pacotes/${id}`),
        onSuccess: () => {
            invalidateCache();
            setAlert('Pacote removido com sucesso!', 'success');
        },
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao remover pacote', 'danger'),
    });

    const transferirPacote = useMutation({
        // Espera: { id, novoClienteId, motivo }
        mutationFn: ({ id, novoClienteId, motivo }) => apiClient.post(`/pacotes/${id}/transferir`, { novoClienteId, motivo }),
        onSuccess: () => {
            invalidateCache();
            setAlert('Pacote transferido com sucesso!', 'success');
        },
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao transferir pacote', 'danger'),
    });

    return {
        addPacote,
        updatePacote,
        deletePacote,
        transferirPacote
    };
};